import type { Cube, TCubeMemberType } from '@cubejs-client/core';
import type { FieldType } from './types';

/**
 * Live Cube `/meta` → builder catalog normalizer.
 *
 * Replaces the hardcoded, drifting `AVAILABLE_CUBES` map (removed in S13). The
 * builder now derives the selectable cubes / measures / dimensions from the real
 * schema returned by `cubeApi.meta()` (the backend proxy serves `/meta`
 * unfiltered, so this is the full catalog). Kept as a pure, node-testable
 * function so the builder's field/measure accessors stay synchronous over an
 * already-resolved catalog.
 */

export type CubeOption = { value: string; label: string };
export type MeasureOption = { value: string; label: string };

export type NormalizedCube = {
  name: string;
  title: string;
  measures: MeasureOption[];
  dimensions: FieldType[];
};

export type NormalizedCubeCatalog = {
  /** Cubes in the order Cube returned them. */
  cubes: NormalizedCube[];
  /** Lookup by fully-qualified cube name, for the synchronous accessors. */
  byName: Record<string, NormalizedCube>;
};

/**
 * Short, human-facing label for a fully-qualified member (`Sales.orderDate` →
 * `orderDate`). Mirrors the label the old `AVAILABLE_CUBES` accessors produced,
 * so the builder UI is unchanged apart from now listing the live catalog.
 */
const shortMemberName = ( memberName: string ): string => memberName.split( '.' )[1] || memberName;

/**
 * Map a Cube member type onto the builder's `FieldType` union. Cube's `time`
 * type is the builder's `datetime` (DateRange keys off it); the other Cube types
 * map through 1:1 and now drive the correct Filters value input (number/boolean)
 * instead of the old always-`string` fallback.
 */
const toFieldType = ( cubeType: TCubeMemberType ): FieldType['type'] => {
  switch ( cubeType ) {
    case 'time': return 'datetime';
    case 'number': return 'number';
    case 'boolean': return 'boolean';
    case 'string': return 'string';
    default: return 'string';
  }
};

const normalizeCube = ( cube: Cube ): NormalizedCube => ({
  name: cube.name,
  title: cube.title,
  measures: cube.measures.map(( measure ) => ({
    value: measure.name,
    label: shortMemberName( measure.name )
  })),
  dimensions: cube.dimensions.map(( dimension ) => ({
    value: dimension.name,
    label: shortMemberName( dimension.name ),
    type: toFieldType( dimension.type )
  }))
});

export function normalizeCubeMeta( cubes: Cube[]): NormalizedCubeCatalog {
  const normalized = cubes.map( normalizeCube );
  const byName = normalized.reduce<Record<string, NormalizedCube>>(( acc, cube ) => {
    acc[cube.name] = cube;
    return acc;
  }, {});
  return { cubes: normalized, byName };
}

export function getCubeOptions( catalog: NormalizedCubeCatalog | null ): CubeOption[] {
  if ( !catalog ) return [];
  return catalog.cubes.map(( cube ) => ({ value: cube.name, label: cube.name }));
}

export function getMeasuresForCube( catalog: NormalizedCubeCatalog | null, cubeName: string ): MeasureOption[] {
  if ( !catalog || !cubeName ) return [];
  return catalog.byName[cubeName]?.measures ?? [];
}

export function getFieldsForCube( catalog: NormalizedCubeCatalog | null, cubeName: string ): FieldType[] {
  if ( !catalog || !cubeName ) return [];
  return catalog.byName[cubeName]?.dimensions ?? [];
}
